/*
  Product mock components for the /apply marketing sections.

  These are faithful, static compositions of the REAL Luxe Studio
  UI — same typography, spacing, tone and section structure — with
  safe demo data (no client PII, no real commercial figures, no
  test credentials, no internal IDs).  They exist to display the
  product at export-quality without embedding heavy authenticated
  bundles or violating tenant privacy.
*/

import { COLORS, SERIF, SANS } from "./_form";
import subtendV2Hero from "@/assets/apply/subtend-v2-concept-1200w.webp";

// ── Reusable frame that mimics a Luxe app window ──────────────────
export function StudioFrame({ children, label, tabs, activeTab, height, dark = false }) {
  const bg = dark ? "#111110" : COLORS.paper;
  const chrome = dark ? "#0B0A09" : "#F0EBDD";
  const chromeBorder = dark ? "rgba(200,169,126,0.14)" : COLORS.cardEdge;
  const textOnChrome = dark ? "rgba(244,239,228,0.72)" : COLORS.inkSoft;
  return (
    <div style={{ background: bg, border: `1px solid ${chromeBorder}`,
                  boxShadow: dark
                    ? "0 40px 80px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(200,169,126,0.06)"
                    : "0 30px 70px -30px rgba(28,28,28,0.35), 0 0 0 1px rgba(226,218,203,0.6)",
                  borderRadius: 6, overflow: "hidden", width: "100%" }}>
      {/* Chrome bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10,
                    background: chrome, padding: "10px 14px",
                    borderBottom: `1px solid ${chromeBorder}` }}>
        <div style={{ display: "flex", gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#E15C4A" }}/>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#E5B34B" }}/>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#7AB27A" }}/>
        </div>
        <div style={{ ...SANS, color: textOnChrome, fontSize: 11.5,
                      letterSpacing: "0.08em", flex: 1, textAlign: "center" }}>
          {label}
        </div>
      </div>
      {/* Tabs (optional) */}
      {tabs && (
        <div style={{ display: "flex", gap: 0, background: dark ? "#0F0E0C" : "#F7F2E5",
                      borderBottom: `1px solid ${chromeBorder}`,
                      padding: "0 12px", overflowX: "auto" }}>
          {tabs.map((t, i) => {
            const on = t === activeTab;
            return (
              <div key={i} style={{ ...SANS, padding: "12px 14px",
                                    fontSize: 12, fontWeight: on ? 600 : 500,
                                    letterSpacing: "0.06em",
                                    color: on ? (dark ? COLORS.gold : COLORS.ink) : (dark ? "rgba(244,239,228,0.5)" : COLORS.inkSoft),
                                    borderBottom: on ? `2px solid ${COLORS.gold}` : "2px solid transparent",
                                    whiteSpace: "nowrap" }}>
                {t}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ height: height || "auto", overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

// ── Six-stage stage rail (used in Project mock) ───────────────────
export function StageRail({ active }) {
  const stages = ["Brief","Design","Visualise","Price","Proposal","Approval"];
  return (
    <div style={{ display: "flex", gap: 0, padding: "12px 14px",
                  background: "#F7F2E5", borderBottom: `1px solid ${COLORS.cardEdge}`,
                  overflowX: "auto", flexWrap: "nowrap" }}>
      {stages.map((s, i) => {
        const on = active === i;
        const done = i < active;
        return (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 8,
                                marginRight: 14, flexShrink: 0 }}>
            <div style={{ ...SANS, width: 22, height: 22, borderRadius: "50%",
                          background: on ? COLORS.ink : (done ? COLORS.gold : "transparent"),
                          border: `1px solid ${on || done ? "transparent" : COLORS.cardEdge}`,
                          color: on ? COLORS.ivory : (done ? COLORS.bgDark : COLORS.inkSoft),
                          fontSize: 11, fontWeight: 700, flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
              {done ? "✓" : String(i + 1).padStart(2, "0")}
            </div>
            <div style={{ ...SANS, fontSize: 11.5, letterSpacing: "0.06em",
                          color: on ? COLORS.ink : COLORS.inkSoft,
                          fontWeight: on ? 600 : 500, whiteSpace: "nowrap" }}>
              {s}
            </div>
            {i < stages.length - 1 && (
              <div style={{ width: 22, height: 1, background: COLORS.cardEdge, marginLeft: 6, flexShrink: 0 }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Design workspace — 2D floor / elevation plan ──────────────────
export function DesignMock() {
  return (
    <StudioFrame label="Subtend Studio · Aldridge Residence · Design"
                 tabs={["Concept 01","Concept 02","Concept 03"]} activeTab="Concept 02">
      <StageRail active={1}/>
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 200px", minHeight: 320 }}>
        {/* Left tool rail */}
        <div style={{ borderRight: `1px solid ${COLORS.cardEdge}`, padding: 14 }}>
          <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                        color: COLORS.goldDeep, fontWeight: 700, marginBottom: 10 }}>
            LAYERS
          </div>
          {["Wall opening","Cabinetry base","TV mount","Timber panels","Stone hearth","Shelving"].map((l, i) => (
            <div key={l} style={{ ...SANS, fontSize: 12, color: COLORS.ink,
                                  padding: "6px 8px", marginBottom: 3,
                                  background: i === 3 ? "#EFE6D2" : "transparent",
                                  borderLeft: i === 3 ? `2px solid ${COLORS.gold}` : "2px solid transparent" }}>
              {l}
            </div>
          ))}
        </div>
        {/* Canvas */}
        <div style={{ background: "#FBFAF3",
                      backgroundImage: "linear-gradient(#EFE9D9 1px, transparent 1px), linear-gradient(90deg, #EFE9D9 1px, transparent 1px)",
                      backgroundSize: "24px 24px", padding: 24, position: "relative" }}>
          {/* Elevation drawing */}
          <svg viewBox="0 0 460 260" style={{ width: "100%", height: "100%",
                                              display: "block", maxHeight: 260 }}>
            <rect x="20" y="16" width="420" height="220" fill="#F4EFE4" stroke="#1C1C1C" strokeWidth="1.5"/>
            <rect x="20" y="200" width="420" height="36" fill="#E4DAC4" stroke="#1C1C1C" strokeWidth="1"/>
            <rect x="150" y="60" width="160" height="80" fill="#0F0E0C" stroke="#8B6F44" strokeWidth="1"/>
            <text x="230" y="106" fontFamily="Manrope" fontSize="9" fill="#C8A97E" textAnchor="middle" letterSpacing="2">75″ SCREEN</text>
            <rect x="30" y="150" width="90" height="50" fill="#D8CBAA" stroke="#1C1C1C" strokeWidth="1"/>
            <rect x="340" y="150" width="90" height="50" fill="#D8CBAA" stroke="#1C1C1C" strokeWidth="1"/>
            <rect x="130" y="150" width="200" height="50" fill="#EEE7D8" stroke="#1C1C1C" strokeWidth="1"/>
            <line x1="150" y1="22" x2="150" y2="60" stroke="#8B6F44" strokeWidth="0.5" strokeDasharray="3 2"/>
            <line x1="310" y1="22" x2="310" y2="60" stroke="#8B6F44" strokeWidth="0.5" strokeDasharray="3 2"/>
            <text x="230" y="18"  fontFamily="Manrope" fontSize="8" fill="#8B6F44" textAnchor="middle">1600 mm</text>
            <text x="24"  y="14"  fontFamily="Manrope" fontSize="8" fill="#8B6F44">2700 mm ceiling</text>
            <text x="75"  y="180" fontFamily="Manrope" fontSize="8" fill="#1C1C1C" textAnchor="middle">Timber base</text>
            <text x="230" y="180" fontFamily="Manrope" fontSize="8" fill="#1C1C1C" textAnchor="middle">Stone hearth</text>
            <text x="385" y="180" fontFamily="Manrope" fontSize="8" fill="#1C1C1C" textAnchor="middle">Storage bay</text>
          </svg>
          {/* dimension pill */}
          <div style={{ position: "absolute", top: 12, right: 16,
                        background: COLORS.ink, color: COLORS.ivory,
                        ...SANS, fontSize: 10, letterSpacing: "0.14em",
                        padding: "5px 10px", fontWeight: 600 }}>
            ELEVATION · 4200 × 2700
          </div>
        </div>
        {/* Right materials rail */}
        <div style={{ borderLeft: `1px solid ${COLORS.cardEdge}`, padding: 14, background: "#F7F2E5" }}>
          <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                        color: COLORS.goldDeep, fontWeight: 700, marginBottom: 10 }}>
            MATERIALS
          </div>
          {[
            { name: "American walnut", sub: "Timber · Veneer", swatch: "#5B3A22" },
            { name: "Nero Marquina",   sub: "Stone · Honed",   swatch: "#22201E" },
            { name: "Silk white",      sub: "2-pac cabinetry",  swatch: "#EFEAD9" },
            { name: "Bronze insert",   sub: "Metal · Brushed",  swatch: "#8A6A3F" },
          ].map(m => (
            <div key={m.name} style={{ display: "flex", gap: 10, alignItems: "center",
                                       padding: "8px 0", borderBottom: `1px solid ${COLORS.cardEdge}` }}>
              <div style={{ width: 30, height: 30, background: m.swatch,
                            border: `1px solid ${COLORS.cardEdge}` }}/>
              <div>
                <div style={{ ...SANS, fontSize: 12, color: COLORS.ink, fontWeight: 600 }}>{m.name}</div>
                <div style={{ ...SANS, fontSize: 10.5, color: COLORS.inkSoft, letterSpacing: "0.06em" }}>{m.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StudioFrame>
  );
}

// ── Template Gallery mock ─────────────────────────────────────────
export function TemplateGalleryMock() {
  const templates = [
    { name: "Modern Signature",           tone: "#3A2A1E", accent: "#C8A97E" },
    { name: "Coastal Hamptons",      tone: "#DDD2BA", accent: "#3E3B2E" },
    { name: "Fireplace Centrepiece", tone: "#1F1B18", accent: "#B0654C" },
    { name: "Floating Minimal",      tone: "#EFEAD9", accent: "#8B6F44" },
    { name: "Full-Wall Joinery",     tone: "#4A3826", accent: "#EFE6D2" },
    { name: "Stone Feature",         tone: "#2A2725", accent: "#B0A48A" },
  ];
  return (
    <StudioFrame label="Subtend Studio · Design Template Gallery">
      <div style={{ padding: 18, background: COLORS.paper }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                          color: COLORS.goldDeep, fontWeight: 700 }}>
              STARTER LIBRARY
            </div>
            <div style={{ ...SERIF, fontSize: 20, color: COLORS.ink, marginTop: 2 }}>
              10 professionally prepared media-wall designs
            </div>
          </div>
          <div style={{ ...SANS, fontSize: 11, color: COLORS.inkSoft,
                        letterSpacing: "0.08em" }}>
            Filter · Sort
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {templates.map(t => (
            <div key={t.name} style={{ border: `1px solid ${COLORS.cardEdge}`,
                                        background: "#fff", overflow: "hidden" }}>
              <div style={{ background: t.tone, height: 82, position: "relative" }}>
                <div style={{ position: "absolute", left: 12, top: 12, right: 12,
                              height: 20, background: `${t.accent}22`,
                              border: `1px solid ${t.accent}66` }}/>
                <div style={{ position: "absolute", left: 20, bottom: 12,
                              width: "62%", height: 24, background: t.accent, opacity: 0.75 }}/>
              </div>
              <div style={{ padding: "10px 12px" }}>
                <div style={{ ...SANS, fontSize: 12, color: COLORS.ink, fontWeight: 600 }}>{t.name}</div>
                <div style={{ ...SANS, fontSize: 10.5, color: COLORS.inkSoft,
                              letterSpacing: "0.06em", marginTop: 2 }}>
                  4200 × 2700 · Adaptable
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StudioFrame>
  );
}

// ── Visualise / 3D mock ───────────────────────────────────────────
export function VisualiseMock() {
  return (
    <StudioFrame dark label="Subtend Studio · Aldridge Residence · Visualise"
                 tabs={["Perspective","Elevation","Section","Camera 01"]} activeTab="Perspective">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", minHeight: 340 }}>
        <div style={{ position: "relative",
                      background: "linear-gradient(180deg, #1C1917 0%, #0B0A09 100%)" }}>
          {/* faux 3D room */}
          <svg viewBox="0 0 640 380" style={{ width: "100%", height: "100%",
                                              display: "block", maxHeight: 340 }}
               preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="floor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#28241E"/>
                <stop offset="1" stopColor="#100E0C"/>
              </linearGradient>
              <linearGradient id="wall" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#3B3229"/>
                <stop offset="1" stopColor="#221D18"/>
              </linearGradient>
              <linearGradient id="walnut" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#5B3A22"/>
                <stop offset="0.5" stopColor="#442A17"/>
                <stop offset="1" stopColor="#5B3A22"/>
              </linearGradient>
            </defs>
            {/* floor */}
            <polygon points="0,300 640,300 640,380 0,380" fill="url(#floor)"/>
            {/* back wall */}
            <polygon points="80,60 560,60 560,300 80,300" fill="url(#wall)"/>
            {/* side walls (perspective) */}
            <polygon points="0,0 80,60 80,300 0,380" fill="#0A0908"/>
            <polygon points="640,0 560,60 560,300 640,380" fill="#0A0908"/>
            {/* ceiling */}
            <polygon points="0,0 640,0 560,60 80,60" fill="#181613"/>
            {/* media wall base */}
            <rect x="130" y="240" width="380" height="60" fill="url(#walnut)"/>
            {/* stone hearth */}
            <rect x="220" y="250" width="200" height="42" fill="#2A2725"/>
            <rect x="222" y="252" width="196" height="4" fill="#3D3835"/>
            {/* screen */}
            <rect x="220" y="130" width="200" height="110" fill="#050403" stroke="#C8A97E" strokeOpacity="0.35"/>
            <rect x="226" y="136" width="188" height="98" fill="#0B0B10"/>
            {/* screen reflection */}
            <polygon points="230,138 400,138 320,180 230,180" fill="#1C1C22" opacity="0.5"/>
            {/* upper shelves */}
            <rect x="130" y="120" width="86" height="8" fill="#5B3A22"/>
            <rect x="424" y="120" width="86" height="8" fill="#5B3A22"/>
            <rect x="130" y="170" width="86" height="8" fill="#5B3A22"/>
            <rect x="424" y="170" width="86" height="8" fill="#5B3A22"/>
            {/* decorative objects */}
            <rect x="145" y="100" width="14" height="20" fill="#8A6A3F"/>
            <circle cx="185" cy="112" r="8" fill="#B0A48A"/>
            <rect x="440" y="98"  width="18" height="22" fill="#2A2725"/>
            <rect x="470" y="105" width="10" height="15" fill="#B0654C"/>
            {/* warm glow */}
            <circle cx="320" cy="90" r="120" fill="#C8A97E" opacity="0.06"/>
          </svg>
          {/* HUD */}
          <div style={{ position: "absolute", left: 14, bottom: 14,
                        ...SANS, fontSize: 10.5, letterSpacing: "0.14em",
                        color: "rgba(244,239,228,0.7)" }}>
            PERSPECTIVE · CAMERA 01 · 24 mm
          </div>
          <div style={{ position: "absolute", right: 14, top: 14,
                        background: "rgba(15,14,12,0.7)",
                        border: "1px solid rgba(200,169,126,0.28)",
                        padding: "4px 9px",
                        ...SANS, fontSize: 10, letterSpacing: "0.14em",
                        color: COLORS.gold, fontWeight: 600 }}>
            LIVE 3D
          </div>
        </div>
        <div style={{ background: "#141210", borderLeft: "1px solid rgba(200,169,126,0.14)",
                      padding: 14 }}>
          <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                        color: COLORS.gold, fontWeight: 700, marginBottom: 12 }}>
            SCENE
          </div>
          {["Camera 01 · 24 mm","Camera 02 · 35 mm","Wall opening","Cabinetry base","Timber panels","Stone hearth","Lighting · Warm"].map((s, i) => (
            <div key={s} style={{ ...SANS, fontSize: 11.5,
                                  color: i === 0 ? COLORS.gold : "rgba(244,239,228,0.68)",
                                  padding: "6px 0", borderBottom: "1px solid rgba(200,169,126,0.10)",
                                  fontWeight: i === 0 ? 600 : 500 }}>
              {s}
            </div>
          ))}
        </div>
      </div>
    </StudioFrame>
  );
}

// ── Price mock ────────────────────────────────────────────────────
export function PriceMock() {
  const lines = [
    { name: "Cabinetry — 2-pac silk white",  cost: "6,420", sell: "9,850", margin: "35%" },
    { name: "Timber panels — American walnut", cost: "3,180", sell: "5,200", margin: "39%" },
    { name: "Stone hearth — Nero Marquina",  cost: "2,240", sell: "3,650", margin: "39%" },
    { name: "AV integration — 75″ + amp",    cost: "4,900", sell: "7,600", margin: "36%" },
    { name: "Install labour · 3 trades",     cost: "3,600", sell: "5,900", margin: "39%" },
  ];
  return (
    <StudioFrame label="Subtend Studio · Aldridge Residence · Price">
      <StageRail active={3}/>
      <div style={{ padding: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 18 }}>
          {/* Line items table */}
          <div style={{ border: `1px solid ${COLORS.cardEdge}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 0.7fr",
                          background: "#F7F2E5",
                          padding: "10px 14px", borderBottom: `1px solid ${COLORS.cardEdge}` }}>
              {["Line item","Cost","Sell","Margin"].map(h => (
                <div key={h} style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                                       color: COLORS.goldDeep, fontWeight: 700 }}>{h}</div>
              ))}
            </div>
            {lines.map((l, i) => (
              <div key={l.name} style={{ display: "grid",
                                          gridTemplateColumns: "1.6fr 1fr 1fr 0.7fr",
                                          padding: "10px 14px",
                                          borderBottom: i === lines.length - 1 ? "none" : `1px solid ${COLORS.cardEdge}` }}>
                <div style={{ ...SANS, fontSize: 12, color: COLORS.ink }}>{l.name}</div>
                <div style={{ ...SANS, fontSize: 12, color: COLORS.inkSoft }}>$ {l.cost}</div>
                <div style={{ ...SANS, fontSize: 12, color: COLORS.ink, fontWeight: 600 }}>$ {l.sell}</div>
                <div style={{ ...SANS, fontSize: 12, color: COLORS.goldDeep, fontWeight: 600 }}>{l.margin}</div>
              </div>
            ))}
          </div>
          {/* Estimate card */}
          <div style={{ background: COLORS.ink, color: COLORS.ivory, padding: 18 }}>
            <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.16em",
                          color: COLORS.gold, fontWeight: 700, marginBottom: 6 }}>
              PROJECT ESTIMATE
            </div>
            <div style={{ ...SERIF, fontSize: 30, lineHeight: 1.1, margin: "6px 0 4px" }}>
              $ 32,200
            </div>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.08em",
                          color: "rgba(244,239,228,0.55)", marginBottom: 16 }}>
              Client sell (ex-GST) · illustrative demo
            </div>
            <div style={{ display: "flex", justifyContent: "space-between",
                          padding: "8px 0", borderTop: "1px solid rgba(244,239,228,0.14)" }}>
              <div style={{ ...SANS, fontSize: 12, color: "rgba(244,239,228,0.68)" }}>Cost</div>
              <div style={{ ...SANS, fontSize: 12, color: COLORS.ivory }}>$ 20,340</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between",
                          padding: "8px 0", borderTop: "1px solid rgba(244,239,228,0.14)" }}>
              <div style={{ ...SANS, fontSize: 12, color: "rgba(244,239,228,0.68)" }}>Margin</div>
              <div style={{ ...SANS, fontSize: 12, color: COLORS.gold, fontWeight: 700 }}>36.8 %</div>
            </div>
            <div style={{ marginTop: 16, ...SANS, fontSize: 11.5, lineHeight: 1.55,
                          color: "rgba(244,239,228,0.55)" }}>
              Internal cost + margin never leave the Studio. Client-facing views show only the investment.
            </div>
          </div>
        </div>
      </div>
    </StudioFrame>
  );
}

// ── Proposal + Client Portal (side-by-side) ───────────────────────
export function ProposalPortalMock() {
  return (
    <div style={{ position: "relative", display: "grid", gap: 18 }}
         className="apply-two-mock">
      {/* Studio proposal */}
      <StudioFrame label="Subtend Studio · Proposal preview">
        <div style={{ padding: 22, background: COLORS.paper }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "flex-start", marginBottom: 14 }}>
            <div>
              <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                            color: COLORS.goldDeep, fontWeight: 700 }}>PROPOSAL · V2</div>
              <div style={{ ...SERIF, fontSize: 22, color: COLORS.ink, marginTop: 4 }}>
                Aldridge Residence · Media wall
              </div>
            </div>
            <div style={{ ...SANS, fontSize: 10.5, letterSpacing: "0.08em",
                          color: COLORS.inkSoft, textAlign: "right" }}>
              Prepared for the Aldridge family<br/>
              Sunshine Coast QLD
            </div>
          </div>
          {[
            { h: "Concept", b: "Full-wall bespoke media joinery in American walnut with a Nero Marquina hearth insert. Recessed 75-inch screen." },
            { h: "Scope",   b: "Design, fabrication, delivery, install and site coordination with your existing trades." },
            { h: "Inclusions", b: "AV integration · Lighting circuit · Cable management · Final finish · On-site rectification." },
          ].map(s => (
            <div key={s.h} style={{ padding: "10px 0",
                                    borderTop: `1px solid ${COLORS.cardEdge}` }}>
              <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                            color: COLORS.goldDeep, fontWeight: 700, marginBottom: 4 }}>{s.h}</div>
              <div style={{ ...SANS, fontSize: 12.5, color: COLORS.ink, lineHeight: 1.55 }}>{s.b}</div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", padding: "14px 0",
                        borderTop: `1px solid ${COLORS.cardEdge}`,
                        borderBottom: `1px solid ${COLORS.cardEdge}` }}>
            <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.14em",
                          color: COLORS.goldDeep, fontWeight: 700 }}>
              CLIENT INVESTMENT
            </div>
            <div style={{ ...SERIF, fontSize: 24, color: COLORS.ink }}>$ 32,200</div>
          </div>
          <div style={{ marginTop: 8, ...SANS, fontSize: 10.5,
                        color: COLORS.inkSoft, letterSpacing: "0.04em" }}>
            Ex-GST · Illustrative demo · No real client data shown.
          </div>
        </div>
      </StudioFrame>

      {/* Client portal */}
      <StudioFrame dark label="Subtend Studio · Client Portal · Aldridge Residence"
                   tabs={["Overview","Concept","Approval"]} activeTab="Approval">
        <div style={{ padding: 22 }}>
          <div style={{ ...SANS, fontSize: 10, letterSpacing: "0.14em",
                        color: COLORS.gold, fontWeight: 700 }}>
            REVIEW · APPROVAL
          </div>
          <div style={{ ...SERIF, fontSize: 22, color: COLORS.ivory,
                        marginTop: 6, marginBottom: 14 }}>
            Please review your media wall concept
          </div>
          {/* Preview card */}
          <div style={{ background: "#1A1815", border: "1px solid rgba(200,169,126,0.18)",
                        padding: 14, marginBottom: 14 }}>
            <div style={{ height: 90, background:
              "linear-gradient(180deg, #3B3229 0%, #181613 60%, #100E0C 100%)",
              position: "relative", marginBottom: 10 }}>
              <div style={{ position: "absolute", left: "30%", top: 18,
                            width: "40%", height: 42, background: "#0B0B10",
                            border: "1px solid rgba(200,169,126,0.25)" }}/>
              <div style={{ position: "absolute", left: "10%", bottom: 8,
                            right: "10%", height: 12, background: "#5B3A22" }}/>
            </div>
            <div style={{ ...SANS, fontSize: 12, color: "rgba(244,239,228,0.82)",
                          lineHeight: 1.55 }}>
              Full-wall bespoke media joinery in walnut, Nero Marquina hearth, 75″ recessed screen.
            </div>
          </div>
          {/* Investment (client-safe) */}
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", padding: "10px 0",
                        borderTop: "1px solid rgba(200,169,126,0.14)",
                        borderBottom: "1px solid rgba(200,169,126,0.14)",
                        marginBottom: 14 }}>
            <div style={{ ...SANS, fontSize: 10.5, letterSpacing: "0.14em",
                          color: "rgba(244,239,228,0.7)", fontWeight: 600 }}>
              YOUR INVESTMENT
            </div>
            <div style={{ ...SERIF, fontSize: 22, color: COLORS.ivory }}>$ 32,200</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ background: COLORS.gold, color: COLORS.bgDark,
                          ...SANS, fontSize: 12, letterSpacing: "0.06em",
                          padding: "10px 16px", fontWeight: 600 }}>
              Accept concept
            </div>
            <div style={{ border: "1px solid rgba(200,169,126,0.28)",
                          color: COLORS.ivory, ...SANS, fontSize: 12,
                          padding: "10px 16px", letterSpacing: "0.06em" }}>
              Request changes
            </div>
          </div>
          <div style={{ marginTop: 14, ...SANS, fontSize: 10.5,
                        color: "rgba(244,239,228,0.55)", letterSpacing: "0.04em" }}>
            Cost, margin and internal notes never appear in the client portal.
          </div>
        </div>
      </StudioFrame>
    </div>
  );
}

// ── Compact hero product composition ─────────────────────────────
export function HeroProductComposition() {
  return (
    <div className="apply-hero-comp"
         style={{ position: "relative", minHeight: 680, isolation: "isolate" }}>
      {/* Warm ambient glow radiating from the AI concept card */}
      <div aria-hidden="true"
           className="apply-hero-glow"
           style={{ position: "absolute", zIndex: 0,
                    top: 40, right: -120, width: 620, height: 500,
                    background:
                      "radial-gradient(55% 55% at 60% 45%, rgba(200,169,126,0.28), transparent 72%)," +
                      "radial-gradient(45% 45% at 40% 65%, rgba(176,101,76,0.14), transparent 72%)",
                    filter: "blur(8px)", pointerEvents: "none" }}/>

      {/* Handwritten "AI visualisation coming in V2" script + curved arrow.
          Sits in the negative space at the top-right, pointing down at
          the tilted AI concept card fanned out behind. */}
      <div className="apply-hero-script"
           aria-hidden="true"
           style={{ position: "absolute", top: -6, right: 20, zIndex: 5,
                    color: COLORS.gold, textAlign: "left",
                    transform: "rotate(-7deg)",
                    fontFamily: "'Caveat', 'Dancing Script', 'Snell Roundhand', cursive",
                    fontSize: 30, lineHeight: 1.1, fontWeight: 500,
                    letterSpacing: "0.005em",
                    pointerEvents: "none" }}>
        <div>AI visualisation</div>
        <div style={{ marginLeft: 22 }}>coming in V2</div>
        <svg width="46" height="52" viewBox="0 0 46 52"
             style={{ display: "block", marginLeft: 96, marginTop: -2 }}>
          <path d="M6 4 C 24 8, 40 22, 30 46"
                stroke={COLORS.gold} strokeWidth="1.5" fill="none"
                strokeLinecap="round"/>
          <path d="M22 38 L30 46 L38 36"
                stroke={COLORS.gold} strokeWidth="1.5" fill="none"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* AI concept card — small + tilted, sitting IN FRONT of the
          Design window like the second card in a fan. Only its
          bottom-left corner overlays the Design window's top-right
          area; the rest fans up-and-to-the-right. */}
      <div className="apply-hero-ai-wrap"
           style={{ position: "absolute", zIndex: 3,
                    top: 40, right: -20,
                    width: 310,
                    transform: "rotate(10deg)",
                    transformOrigin: "left bottom",
                    border: `2px solid ${COLORS.gold}`,
                    boxShadow:
                      "0 0 60px rgba(200,169,126,0.32), " +
                      "0 50px 100px -20px rgba(0,0,0,0.85), " +
                      "inset 0 0 0 1px rgba(200,169,126,0.15)",
                    background: "#0B0A09", overflow: "hidden" }}
           aria-hidden="true">
        <div style={{ position: "relative", aspectRatio: "4 / 5" }}>
          <img src={subtendV2Hero}
               alt=""
               loading="eager" decoding="async"
               style={{ position: "absolute", inset: 0, width: "100%",
                        height: "100%", objectFit: "cover", display: "block" }}/>
          {/* CONCEPT PREVIEW ribbon */}
          <div style={{ ...SANS, position: "absolute", right: 8, bottom: 8,
                        background: "rgba(11,10,9,0.86)", color: COLORS.gold,
                        border: `1px solid rgba(200,169,126,0.55)`,
                        padding: "4px 9px", fontSize: 8.5,
                        letterSpacing: "0.22em", fontWeight: 700,
                        textTransform: "uppercase",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(4px)" }}>
            Concept preview
          </div>
        </div>
      </div>

      {/* Design window — main card, straight, sits behind the AI card
          at the top-right corner only. */}
      <div className="apply-hero-window"
           style={{ position: "absolute", zIndex: 2,
                   top: 200, left: -16,
                   width: "102%", maxWidth: 600,
                   boxShadow:
                     "0 50px 100px -30px rgba(0,0,0,0.9), " +
                     "0 0 0 1px rgba(200,169,126,0.10)" }}>
        <DesignMock/>
        {/* Project Estimate — kissed to the Design window's bottom-right */}
        <div className="apply-hero-float"
             style={{ position: "absolute", right: -28, bottom: -28, zIndex: 3,
                      width: 210, background: COLORS.ink, color: COLORS.ivory,
                      padding: "14px 16px",
                      boxShadow:
                        "0 30px 60px -20px rgba(0,0,0,0.9), " +
                        "0 0 0 1px rgba(200,169,126,0.22)",
                      border: `1px solid rgba(200,169,126,0.42)` }}>
          <div style={{ ...SANS, fontSize: 9, letterSpacing: "0.22em",
                        color: COLORS.gold, fontWeight: 700 }}>PROJECT ESTIMATE</div>
          <div style={{ ...SERIF, fontSize: 26, marginTop: 4, letterSpacing: "-0.01em" }}>$ 32,200</div>
          <div style={{ ...SANS, fontSize: 10, color: "rgba(244,239,228,0.6)",
                        letterSpacing: "0.04em", marginTop: 2 }}>
            Client sell · Ex-GST
          </div>
          <div style={{ display: "flex", justifyContent: "space-between",
                        marginTop: 10, paddingTop: 8,
                        borderTop: "1px solid rgba(244,239,228,0.14)" }}>
            <div style={{ ...SANS, fontSize: 10.5, color: "rgba(244,239,228,0.66)" }}>Margin</div>
            <div style={{ ...SANS, fontSize: 10.5, color: COLORS.gold, fontWeight: 700 }}>36.8 %</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Material starter swatches ────────────────────────────────────
export function MaterialsGrid() {
  const materials = [
    { name: "American walnut",    cat: "Timber",    swatch: "#5B3A22" },
    { name: "Blackbutt",           cat: "Timber",    swatch: "#8A5F35" },
    { name: "Silver ash",          cat: "Timber",    swatch: "#B49F76" },
    { name: "Nero Marquina",       cat: "Stone",     swatch: "#22201E" },
    { name: "Carrara honed",       cat: "Stone",     swatch: "#E6E1D6" },
    { name: "Basaltina",           cat: "Stone",     swatch: "#3C3833" },
    { name: "Sandstone form",      cat: "Concrete",  swatch: "#A69479" },
    { name: "Charcoal render",     cat: "Concrete",  swatch: "#3A3833" },
    { name: "Acoustic slat oak",   cat: "Acoustic",  swatch: "#7A5A38" },
    { name: "Acoustic felt grey",  cat: "Acoustic",  swatch: "#565351" },
    { name: "Silk white 2-pac",    cat: "Cabinetry", swatch: "#EFEAD9" },
    { name: "Warm bronze insert",  cat: "Panels",    swatch: "#8A6A3F" },
  ];
  return (
    <div className="apply-materials-grid"
         style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
      {materials.map(m => (
        <div key={m.name}
             style={{ display: "flex", gap: 12, alignItems: "center",
                      background: COLORS.paper, padding: "10px 12px",
                      border: `1px solid ${COLORS.cardEdge}` }}>
          <div style={{ width: 34, height: 34, background: m.swatch,
                        border: `1px solid ${COLORS.cardEdge}` }}/>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...SANS, fontSize: 12.5, color: COLORS.ink, fontWeight: 600 }}>{m.name}</div>
            <div style={{ ...SANS, fontSize: 10.5, color: COLORS.inkSoft,
                          letterSpacing: "0.06em" }}>{m.cat}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
